import { ProjectActionContext } from "@components/common/project-actions";
import {
  BaseProjectIdProps,
  GroupId,
  ProjectId,
} from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { GUID } from "@faro-lotv/ielement-types";
import { LinkProps } from "@mui/material";
import defaultThumbnail from "@src/assets/sdb-default-project-thumbnail_1.svg";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DateTimeUtils, WebEditorUtils } from "@stellar/utils";
import { formatAreaUnit, getPrettyDateElapsed } from "@utils/data-display";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { UseTrackEvent } from "@utils/track-event/use-track-event";
import { DASH, formatStorageSize, getStorageUnit } from "@utils/ui-utils";
import { getPrettyName } from "@utils/user-utils";

/** Default project thumbnail image */
export const defaultProjectThumbnail = defaultThumbnail;

/** Defines the minimum length for the project name */
export const MIN_PROJECT_NAME_LENGTH = 1;

/** Defines the default project size area in sqft to be used when not provided by the the user */
export const DEFAULT_PROJECT_AREA = 1;

/** Defines the minimum number of characters to start a project search */
export const MIN_CHARACTERS_FOR_PROJECT_SEARCH = 2;

/** Specifies the target attribute value for opening the project. */
export const OPEN_PROJECT_TARGET_ATTRIBUTE: LinkProps["target"] = "_self";

/** Specifies the target attribute value for opening other elements annotations, etc. */
export const OPEN_OTHERS_TARGET_ATTRIBUTE: LinkProps["target"] = "_blank";

/**
 * Sphere Viewer deep link format:
 * https://faro01.atlassian.net/wiki/spaces/SWEB/pages/3717529850/Sphere+Viewer+Deep+Link+Format
 */
interface GetSphereViewerUrlParamsProps {
  /** ID of the IElement to orient the camera towards to */
  lookAtId?: GUID;

  /** ID of the IElement being visualized */
  id?: GUID;
}

export interface GetDataManagementUrlParamsProps {
  /** Enable the handling of developer features. */
  shouldEnableDev?: boolean;
  /** Enable the handling of alpha features. */
  shouldEnableAlpha?: boolean;
  /** Enable the handling of beta features. */
  shouldEnableBeta?: boolean;
  /** Show the dropzone view for uploading at the start whenever it's allowed to upload something. */
  shouldShowUpload?: boolean;
  /** Show the publish panel whenever at the start it's allowed to publish the project. */
  shouldShowPublish?: boolean;
}

/**
 * @returns the query parameters required to build a valid Sphere Viewer deep link that
 * opens the project and shows the element specified by the ID of the element.
 */
function getSphereViewerUrlParams({
  lookAtId,
  id,
}: GetSphereViewerUrlParamsProps): URLSearchParams {
  const params = new URLSearchParams();

  if (lookAtId) {
    params.append("lookAtId", lookAtId);
  }
  if (id) {
    params.append("id", id);
  }

  return params;
}

export function getWebEditorUrl(projectId: ProjectId): URL {
  return new URL(
    WebEditorUtils.generateWebEditorUrl({
      baseUrl: runtimeConfig.urls.hbWebEditorUrl,
      projectId,
      mode: "editor",
    })
  );
}

interface GetSphereViewerUrlProps extends GetSphereViewerUrlParamsProps, BaseProjectIdProps {
  additionalParams?: URLSearchParams;
}

/**
 * Returns the URL to open a project in the Sphere Viewer.
 */
export function getSphereViewerUrl({
  projectId,
  lookAtId,
  id,
  additionalParams,
}: GetSphereViewerUrlProps): URL {
  const url = new URL(
    `project/${projectId}`,
    runtimeConfig.urls.sphereViewerUrl
  );

  const params = getSphereViewerUrlParams({
    lookAtId,
    id,
  });

  if (additionalParams) {
    for (const [key, value] of additionalParams) {
      params.append(key, value);
    }
  }

  url.search = params.toString();
  return url;
}

/**
 * Returns the URL to open the Data Management editor in the Sphere Viewer.
 */
export function getDataManagementUrl({
  projectId,
  shouldEnableDev,
  shouldEnableAlpha,
  shouldEnableBeta,
  shouldShowUpload,
  shouldShowPublish,
}: BaseProjectIdProps & GetDataManagementUrlParamsProps): URL {
  const url = new URL(
    `/data-management/project/${projectId}`,
    runtimeConfig.urls.sphereViewerUrl
  );

  const params = new URLSearchParams();
  if (shouldEnableDev) {
    params.append("enableDev", "true");
  }
  if (shouldEnableAlpha) {
    params.append("enableAlpha", "true");
  }
  if (shouldEnableBeta) {
    params.append("enableBeta", "true");
  }
  if (shouldShowUpload) {
    params.append("showUpload", "true");
  }
  if (shouldShowPublish) {
    params.append("showPublish", "true");
  }
  url.search = params.toString();

  return url;
}

export type OpenProjectProps = GetSphereViewerUrlProps &
  Pick<UseTrackEvent, "trackAsyncEvent"> & {
    /** To recognize exactly where the project is open from */
    openFrom:
      | ProjectActionContext
      | "demo-project-empty-page"
      | "projectDetails"
      | "projectDetails-annotations"
      | "projectDetails-data-pointClouds"
      | "breadcrumbMenu"
      | "projectDetails-manageDataButton"
      | "dataManagement-redirect"
      | "projects-menu-option-mobile";

    /** Number of members in the project to use as props in tracking event. */
    numberOfMembers: number | undefined;

    /** Application target to open the project, either Sphere Viewer or WebEditor */
    openTarget?: ProjectLaunchTarget;

    /**
     * Defines the click that opened the project.
     * Click means the left mouse button, auxClick means the middle mouse button,
     * and contextMenu means the right mouse button.
     */
    clickType?:
      | "left button click"
      | "middle button click"
      | "open context menu";
  };

type TrackProjectDateProps = {
  /** Optional date when the project was last modified */
  modifiedAt?: number;

  /** Optional date when the project was created */
  createdAt?: number;
};

type TrackProjectProps = Pick<
  OpenProjectProps,
  | "openFrom"
  | "numberOfMembers"
  | "openTarget"
  | "clickType"
  | "trackAsyncEvent"
> &
  TrackProjectDateProps &
  Partial<Pick<SdbProject, "accessLevel">>;

type TrackProjectDateResult = {
  /** Returns the day in numbers from the last time a project was updated */
  lastUpdateAge?: number;

  /**
   * Returns the day in words from the last time a project was updated.
   * It combines to have 1 week, 2 weeks, 1 month, 2 months, etc.
   */
  lastUpdateAgeRange?: string;

  /** Returns the day in numbers from the creation date of the project */
  creationAge?: number;

  /**
   * Returns the day in words from the creation date of the project.
   * It combines to have 1 week, 2 weeks, 1 month, 2 months, etc.
   */
  creationAgeRange?: string;
};

/**
 * Gets the props related to the creation and last update date of a project to be used in tracking events.
 */
export function getTrackProjectDateProps({
  modifiedAt,
  createdAt,
}: TrackProjectDateProps): TrackProjectDateResult {
  const eventProps: TrackProjectDateResult = {};

  if (modifiedAt) {
    eventProps.lastUpdateAge = DateTimeUtils.getAge(modifiedAt);
    eventProps.lastUpdateAgeRange = DateTimeUtils.getDisplayAge(
      eventProps.lastUpdateAge
    );
  }

  if (createdAt) {
    eventProps.creationAge = DateTimeUtils.getAge(createdAt);
    eventProps.creationAgeRange = DateTimeUtils.getDisplayAge(
      eventProps.creationAge
    );
  }

  return eventProps;
}

/**
 * Tracks whenever a user wants to open a project in the Sphere Viewer or WebEditor.
 * This function does not open the project, it only tracks the event.
 *
 * @param openFrom
 */
export async function trackOpenProject({
  openFrom,
  numberOfMembers,
  openTarget = ProjectLaunchTarget.sphereViewer,
  clickType,
  trackAsyncEvent,
  modifiedAt,
  createdAt,
  accessLevel,
}: TrackProjectProps): Promise<void> {
  const shouldOpenInSV = openTarget === ProjectLaunchTarget.sphereViewer;

  const eventProps: Record<string, string | number> = {
    openFrom: openFrom,
    ...getTrackProjectDateProps({ modifiedAt, createdAt }),
  };
  if (numberOfMembers) {
    eventProps.numberOfMembers = numberOfMembers;
  }

  if (shouldOpenInSV) {
    eventProps.openTarget = ProjectLaunchTarget.sphereViewer;
  } else {
    eventProps.openTarget = ProjectLaunchTarget.webEditor;
  }

  if (clickType) {
    eventProps.clickType = clickType;
  }

  if (accessLevel) {
    eventProps.accessLevel = accessLevel;
  }

  await trackAsyncEvent({
    name: ProjectEvents.openProject,
    props: eventProps,
  });
}

type TrackShareProjectProps = Partial<
  Pick<SdbProject, "accessLevel" | "createdAt" | "modifiedAt">
> &
  Pick<UseTrackEvent, "trackEvent"> & {
    /**
     * Defines where did this event was originated.
     */
    eventLocation: string;
  };

/**
 * Tracks in Amplitude whenever a user clicks the button to share a project
 */
export function trackShareProject({
  accessLevel,
  createdAt,
  modifiedAt,
  eventLocation,
  trackEvent,
}: TrackShareProjectProps): void {
  trackEvent({
    name: ProjectEvents.shareProject,
    props: {
      // Access level seems to be always available in the project object,
      // but the type definition might not be up to date that the compilers does not agree.
      // TODO: https://faro01.atlassian.net/browse/ST-2226 to update the type definition
      accessLevel: accessLevel ?? "unknown",
      eventLocation,
      ...getTrackProjectDateProps({
        createdAt,
        modifiedAt,
      }),
    },
  });
}

/**
 * Gets the URL to open the project for the provided target.
 */
export function getOpenProjectHref({
  openTarget,
  projectId,
  additionalParams,
}: Pick<
  OpenProjectProps,
  "openTarget" | "projectId" | "additionalParams"
>): string {
  return openTarget === ProjectLaunchTarget.sphereViewer
    ? getSphereViewerUrl({
        projectId,
        additionalParams,
      }).href
    : getWebEditorUrl(projectId).href;
}

/**
 * Gets the display name for the provided target.
 */
export function getOpenProjectTargetName(target: ProjectLaunchTarget): string {
  return target === ProjectLaunchTarget.sphereViewer
    ? "Sphere XG"
    : "HoloBuilder";
}

/**
 * Opens a project in one of the viewer apps (Sphere Viewer or WebEditor). Which one is
 * dependant on the openTarget prop.
 */
export async function openProjectInViewer({
  project,
  openFrom,
  numberOfMembers,
  openTarget = ProjectLaunchTarget.sphereViewer,
  lookAtId,
  id,
  clickType,
  trackAsyncEvent,
}: Omit<OpenProjectProps, "projectId"> & {
  project: SdbProject;
}): Promise<void> {
  const shouldOpenInSV = openTarget === ProjectLaunchTarget.sphereViewer;
  const projectId = project.id;

  await trackOpenProject({
    openFrom,
    numberOfMembers,
    openTarget,
    clickType,
    modifiedAt: project.modifiedAt,
    createdAt: project.createdAt,
    trackAsyncEvent,
  });

  const targetUrl = shouldOpenInSV
    ? getSphereViewerUrl({ projectId, lookAtId, id })
    : getWebEditorUrl(projectId);

  window.location.assign(targetUrl.href);
}

/**
 * Converts to the simplified project archiving state from the more elaborated ArchivingState.
 *
 * @param archivingState Original archiving state, e.g. "ARCHIVED_DOWNLOADED"
 * @returns The simplified version of the archiving state, e.g. "active".
 */
export function getProjectArchivingState(
  archivingState: FaroCoreApiTypes.ArchivingState
): ProjectArchivingState {
  switch (archivingState) {
    case "ARCHIVED":
    case "ARCHIVED_DOWNLOADED":
    case "ARCHIVED_READY_TO_DOWNLOAD":
      return ProjectArchivingState.archived;
    case "UNARCHIVED":
      return ProjectArchivingState.active;
    default:
      return archivingState;
  }
}

/**
 * Convert the readable project state to the API archiving state
 */
export function projectStateToApiArchivingState(
  projectArchivingState: ProjectArchivingState
): FaroCoreApiTypes.ArchivingState[] {
  let archivingStates: FaroCoreApiTypes.ArchivingState[] = ["UNARCHIVED"];
  if (projectArchivingState === ProjectArchivingState.archived) {
    archivingStates = [
      "ARCHIVED",
      "ARCHIVED_DOWNLOADED",
      "ARCHIVED_READY_TO_DOWNLOAD",
    ];
  }

  return archivingStates;
}

/**
 * Returns the pretty version of the last time a project was modified.
 *
 * @param project The project to get its modifiedAt property.
 * @returns The last modified date. E.g. 2 days ago.
 */
export function getProjectModifiedElapsed(project: SdbProject): string {
  return getPrettyDateElapsed(project.modifiedAt);
}

/**
 * Returns the pretty version of the created time a project was created.
 *
 * @param project The project to get its createdAt property.
 * @returns The created date. E.g. 2 days ago.
 */
export function getProjectCreatedElapsed(project: SdbProject): string {
  return getPrettyDateElapsed(project.createdAt);
}

/**
 * Returns the name of the group the project belongs to.
 */
export function getProjectGroupName(project: SdbProject): string {
  return project.group?.name ?? DASH;
}

/**
 * Returns the id of the group the project belongs to.
 */
export function getProjectGroupId(project: SdbProject): GroupId | null {
  return project.group?.id ?? null;
}

/**
 * Returns true if a project is a demo.
 */
export function isProjectDemo(project: SdbProject): boolean {
  const demoTag: FaroCoreApiTypes.SystemTag = "sys:demo";
  return project.tags.includes(demoTag);
}

/**
 * Gets the project manager for the provided project.
 *
 * @param project Project to extract its project manager from.
 * @returns The project manager, null otherwise.
 */
export function getProjectManager(project: SdbProject): SdbMemberTypes | null {
  // TODO: we don't yet support UserAsManager in MemberTypes: https://faro01.atlassian.net/browse/ST-1721, ST-646
  return (project.managers.projectManager as SdbMemberTypes) ?? null;
}

/**
 * Gets the name of the project manager for the provided project.
 *
 * @param project Project to extract its project manager from.
 * @returns The project manager, a dash otherwise.
 */
export function getProjectManagerName(project: SdbProject): string {
  return getPrettyName(getProjectManager(project)) || DASH;
}

/** Returns the project's client name */
export function getProjectClientName(project: SdbProject): string {
  return project.clientName ?? DASH;
}

/** Returns the project assigned area */
export function getProjectArea(project: SdbProject): string {
  return project.area
    ? `${project.area.amount} ${formatAreaUnit(project.area.unit)}`
    : DASH;
}

/** Returns the project size */
export function getProjectSize(project: SdbProject): string {
  const size = project.storage.size;
  if (!size) {
    return DASH;
  }

  const currentToDisplay = formatStorageSize(size).toFixed(2);
  const currentUnit = getStorageUnit(size);

  return `${currentToDisplay} ${currentUnit}`;
}

/**
 * @returns whether the project archiving state is "downloaded"
 * @param project Project entity
 */
export function isProjectDownloaded(project: SdbProject): boolean {
  return project.archivingState === "ARCHIVED_DOWNLOADED";
}

/**
 * @returns true if open project button should be hidden
 * @param project Project entity
 *
 * Open project button will be hidden if
 * - The project is archived and user has not the resource owner role
 */
export function shouldHideOpenProject(project: SdbProject | null): boolean {
  if (!project) {
    return false;
  }

  const isProjectArchived =
    getProjectArchivingState(project.archivingState) ===
    ProjectArchivingState.archived;

  return isProjectArchived && project.role !== "RESOURCE_OWNER";
}

/** Check whether a feature exist in a project or not */
export function isFeatureEnabled(
  project: SdbProject,
  feature: FaroCoreApiTypes.FeatureIdentifier
): boolean {
  return !!project.features?.[feature]?.enabled;
}

/**
 * @returns true if the search query is valid according to the backend requirements
 * @param search Search query to validate
 */
export function isValidProjectSearchQuery(search?: string): boolean {
  return (
    search !== undefined &&
    search.trim().length >= MIN_CHARACTERS_FOR_PROJECT_SEARCH
  );
}
